/**
 * Helper functions
 */

import moment from "moment";
import PasswordValidator from "password-validator";
import User from "../services/user.jsx";
import debounce from "lodash/debounce";

class HelpersClass {

	/**
	 * Init helpers
	 */
	constructor(server) {
		this.server = server;
		this.userPermissions = User.permissions() || {};
	}

	/**
	 * Check if value is string
	 */
	isString(value){
		return typeof value == 'string';
	}

	/**
	 * Check if value is array
	 */
	isArray(value){
		return Array.isArray(value);
	}
	
	/**
	 * Check if value is object
	 */
	isObject(value){
		return value && value.constructor === Object;
	}
	
	/**
	 * Check if value is boolean
	 */
	isBoolean(value){
		return typeof value == 'boolean';
	}
	
	/**
	 * Check if value is numeric (number)
	 */
	isNumeric(value){
		return typeof value == 'number'
	}
	
	/**
	 * Check if value is numeric and integer
	 */
	isInteger(value){
		return this.isNumeric(value) && Number.isInteger(value);
	}
	
	/**
	 * Check if value is numberic and float
	 */
	isFloat(value){
		return this.isNumeric(value) && !Number.isInteger(value);
	}
	
	/**
	 * Check if value is a function
	 */
	isFunction(value){
		return typeof value == 'function';
	}
	
	/**
	 * Check if value is an email
	 */
	isEmail(value){
		return /^(([^<>()[\]\\.,;:\s@\"]+(\.[^<>()[\]\\.,;:\s@\"]+)*)|(\".+\"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/.test(value);
	}
	
	/**
	 * Check if variable is empty (null or undefined)
	 */
	isEmpty(value) {
		return value === undefined || value === null;
	}

	/**
	 * Check if given variable is a timestamp
	 */
	isTimestamp(timestamp) {
		if(this.isNumber(timestamp)) {
			return (new Date(timestamp)).getTime() > 0;
		}
		return false;
	}

	/**
	 *  Format Date
	 */
	formatDate(dateStr, format) {
		return moment(dateStr).format(format);
	}
	/**
	 * Extend object a values with object b values
	 */
	extend(a, b) {
		for (var key in b)
			a[key] = b[key];
		return a;
	}

	/**
	 * Index entities into id => entity object
	 */
	index(entities, mappingKey) {
		let obj = {}
		if(!mappingKey)
			mappingKey = 'id'

		entities.forEach(entity => {
			obj[entity[mappingKey]] = entity
		})
		return obj
	}

	/**
	 * Bytes to KB / MB / GB / TB...
	 */
	formatBytes(bytes, decimals = 2) {
		if (bytes === 0) return '0 Bytes';

		const k = 1024;
		const dm = decimals < 0 ? 0 : decimals;
		const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];

		const i = Math.floor(Math.log(bytes) / Math.log(k));

		return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
	}

	lessThan1GB(bytes) {
		return bytes < 1 * 1024 * 1024 * 1024;
	}

	formatTime(seconds) {
		const format = val => `0${Math.floor(val)}`.slice(-2)
		const hours = seconds / 3600
		const minutes = (seconds % 3600) / 60

		return [hours, minutes, seconds % 60].map(format).join(':')
	}

	calculateLayoutByDay(events) {
		// Helper function to parse date strings
		const parseDate = (dateStr) => new Date(dateStr);
	
		// Group events by day
		const groupedByDay = {};
		events.forEach((event) => {
			const startDate = parseDate(event.startTime).toISOString().split('T')[0];
			if (!groupedByDay[startDate]) {
				groupedByDay[startDate] = [];
			}
			groupedByDay[startDate].push({
				...event,
				start: parseDate(event.startTime),
				end: parseDate(event.endTime),
			});
		});
	
		const result = [];
	
		// Process each day's events
		Object.entries(groupedByDay).forEach(([date, dayEvents]) => {
			dayEvents.sort((a, b) => a.startTime - b.startTime || a.endTime - b.endTime);
	
			const columns = []; // Track each column's end time
			dayEvents.forEach((event) => {
				let columnAssigned = false;
	
				// Try to assign event to an existing column
				for (let i = 0; i < columns.length; i++) {
					if (event.start >= columns[i]) {
						columns[i] = event.end; // Update the column's end time
						event.column = i;
						columnAssigned = true;
						break;
					}
				}
	
				// If no column could accommodate the event, create a new column
				if (!columnAssigned) {
					event.column = columns.length;
					columns.push(event.end);
				}
			});
	
			// Total columns for the day
			const totalColumns = columns.length;
			const columnWidth = 100 / totalColumns;
	
			// Calculate width and leftOffset for each event
			dayEvents.forEach((event) => {
				event.width = columnWidth;
				event.leftOffset = event.column * columnWidth;
				result.push(event);
			});
		});
	
		return result;
	}
	DEVICES_TYPE_MAP = {
		'vox': 'VOX tablet',
		'other': 'Other',
	}
	CAMPAIGN_STATUS_MAP = {
		off: {
			label: 'Off',
			labelColor: '#FF4646'
		},
		on: {
			label: 'Ongoing',
			labelColor: '#2CDB5D'
		},
		upcoming: {
			label: 'Upcoming',
			labelColor: '#2196F3',
		}
	}
	getCampaignStatusOptions(){
		return Object.keys(this.CAMPAIGN_STATUS_MAP).map(key => ({id: key, name: this.CAMPAIGN_STATUS_MAP?.[key]?.label}))
	}
	PLAYLIST_PLAYMODE_MAP = {
		'normal': "Normal",
		'random': "Random",
		'smart_play': "Smart play",
	}
	ADMIN_PLAY_LIST_TYPES = {
		vox: "VOX Playlist",
		custom: "Tailor-made",
		sr_library: "SR Library",
		affiliate: "Affiliate playlist"
	}
	PLAY_LIST_TYPES = {
		affiliate: "Affiliate playlist",
	}
	getPlaylistTypeLabel(type){
		return this.ADMIN_PLAY_LIST_TYPES?.[type] || '-'
	}
	getPlaylistTypeOptions(canManageAllTypes = this.isSuperAdmin()){
		return this.mapToOptions(
			canManageAllTypes ? this.ADMIN_PLAY_LIST_TYPES : this.PLAY_LIST_TYPES
		)
	}
	getPlaylistTypeFilterOptions(){
		return Object.entries(this.ADMIN_PLAY_LIST_TYPES).map(([id, name]) => ({ id, name }))
	}
	mapToOptions(object){
		if(!object || typeof object !== 'object') return [];
		return Object.entries(object).map(([key, value]) => ({label: value, key}))
	}
	caseInsensitiveAlphabeticalOrder(a, b){ return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })}
	cleanObject(obj) {
		return Object.fromEntries(
			Object.entries(obj).filter(([_, value]) => value !== '' && value !== undefined)
		);
	}
	DEFAULT_NEW_CAMPAIGN = {
	  name: 'New campaign',
	  infinite: true,
	  priority: 1,
	  voiceOvers: [],
	  schedules: [],
	  devices: []
	}
	CAMPAIGN_PRIORITY = {
		1: 1,
		2: 2,
		3: 3,
	}
	COLLECTION_MODE_MAP = {
		full: "Full",
		partial: 'Partial'
	}
	OVERALL_PERMISSION = {
		global_client_access: "Global Client Access",
		affiliate_managed_clients: "Affiliate-Managed Clients",
		specified_clients: "Specified Clients",
	}
	PLAYLIST_ADD_DELETE_PERMISSION = {
		global_playlist_access: "Global Playlist Access",
		affiliate_managed_playlists: "Affiliate-Managed Playlists",
		self_created_playlists: "Self-Created Playlists",
	}
	CLIENTS_PERMISSIONS = {
		"clients:list": "View clients list",
		"clients:details": "View clients details",
		"clients:subtab:campaigns": "View campaign subtab",
		"clients:subtab:devices": "View devices subtab",
		"clients:subtab:playlists": "View playlists subtab",
		"clients:subtab:collections": "View collections subtab",
		"clients:edit": "Create/edit a client",
		"clients:delete": "Delete a client",
	}
	DEVICES_PERMISSIONS = {
		"devices:list": "View devices list",
		"devices:edit": "Create/edit a device",
		"devices:delete": "Delete a device",
		"devices:details": "View device detail",
	}
	CAMPAIGNS_PERMISSIONS = {
		"campaigns:list": "View campaigns list",
		"campaigns:details": "View campaign details",
		"campaigns:edit": "Create/edit/duplicate a campaign",
		"campaigns:delete": "Delete a campaign",
	}
	PLAYLISTS_PERMISSIONS = {
		"playlists:list": "View playlists list",
		"playlists:details": "View playlist detail",
		"playlists:edit": "Create/edit a playlist",
		"playlists:media": "Add/delete a media",
		"playlists:delete": "Delete a playlist",
	}
	COLLECTIONS_PERMISSIONS = {
		"collections:list": "View collections list",
		"collections:details": "View collections detail",
		"collections:edit": "Create/edit a collection",
		"collections:playlists": "Add/delete a playlist",
		"collections:delete": "Delete a collection",
	}
	USERS_PERMISSIONS = {
		"users:list": "View users list",
		"users:details": "View user details",
		"users:edit": "Create/edit a user",
		"users:delete": "Disable/delete a user",
	}
	AFFILIATES_PERMISSIONS = {
		"affiliates:list": "View affiliates list",
		"affiliates:details": "View affiliates detail",
		"affiliates:edit": "Create/edit a affiliate",
		"affiliates:delete": "Delete a affiliate",
	}
	ROLES_PERMISSIONS = {
		"roles:list": "View roles list",
		"roles:details": "View roles detail",
		"roles:edit": "Edit a role",
	}
	APK_PERMISSIONS = {
		"apk:upload": "Upload new APK",
	}
	isValidPassword(password) {
		const validator = new PasswordValidator();
		validator.is().min(8)
			.has().letters()
			.has().digits()
			.has().symbols();
		const failedRules = validator.validate(password, { details: true });

		return failedRules.length === 0;
	}
	isCurator(role = this.userPermissions?.role) {
		return role === 'Curator';
	}
	isSuperAdmin(role = this.userPermissions?.role) {
		return role === "Super Admin";
	}
	isAffiliateAdmin(role = this.userPermissions?.role) {
		return role === "Affiliate Admin";
	}
	isAffiliateManager(role = this.userPermissions?.role) {
		return role === 'Affiliate Admin';
	}
	isAccountManager(role = this.userPermissions?.role) {
		return role === "Account Manager";
	}
	isClient(role = this.userPermissions?.role) {
		return role === "Client";
	}
	checkPermissions(requiredPermissions, or = false) {
		if (!this.isArray(requiredPermissions)) {
			requiredPermissions = [requiredPermissions];
		}
		if (or) {
			return requiredPermissions.some(
				permission => this.userPermissions?.permissions?.includes(permission)
			);
		} else {
			return requiredPermissions.every(
				permission => this.userPermissions?.permissions?.includes(permission)
			);
		}
	}
	handleFilters(that, filters, entities) {
		entities?.filters?.availableFilters?.forEach((filter) => {
			switch (filter.type) {
				case "text":
					filters.push({
						type: "text",
						label: filter.placeholder || `Search for ${filter.name}`,
						onChange: debounce((value) => {
							if (value !== that.state.filters[filter.name]) {
								const newFilter = Helpers.cleanObject({
									...that.state.filters,
									[filter.name]: value,
								});
								that.setState({ filters: newFilter }, () => {
									that.loadEntities();
								});
							}
						}, 500),
					});
					break;
				case "boolean":
					filters.push({
						type: "select",
						isClearable: true,
						placeholder: filter.placeholder || `Select ${filter.name}`,
						entities: [
							{
								id: true,
								name: "Yes",
							},
							{
								id: false,
								name: "No",
							}
						],
						onChange: (value) => {
							if (value !== that.state.filters[filter.name]) {
								const newFilter = Helpers.cleanObject({
									...that.state.filters,
									[filter.name]: value?.id,
								});
								that.setState(
									{
										filters: newFilter,
									},
									() => {
										that.loadEntities();
									}
								);
							}
						},
					});
					break;
				case "singleSelect":
					filters.push({
						type: "select",
						isClearable: true,
						placeholder: filter.placeholder || `Select ${filter.name}`,
						entities: filter.values.map((value) => ({
							id: value.value,
							name: value.label,
						})),
						onChange: (value) => {
							if (value !== that.state.filters?.[filter.name]) {
								const newFilter = Helpers.cleanObject({
									...that.state.filters,
									[filter.name]: value?.id,
								});
								that.setState(
									{
										filters: newFilter,
									},
									() => {
										that.loadEntities();
									}
								);
							}
						},
					});
					break;
				default:
					break;
			}
		});
	}
}

const Helpers = new HelpersClass()

export default Helpers
